import { Component, Input, Output, EventEmitter } from '@angular/core';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { SearchDisplayType } from '@sales/modules/mobilephone/models/mobilephone-search-enums';
import { DealerClaimType } from '@core/enums/dealer-claim-type';
import { HardwareVariantGroup } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { AccessoryItem } from '@app/sales/models/accessory.model';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { select } from '@ngxs/store';
import { IconNames } from '@app/shared/enums/icon-names';
import { PriceFormatPipe } from '../../pipes/price-format.pipe';
import { AllowSettingsDirective } from '../../directives/allow-settings.directive';
import { NgClass } from '@angular/common';
import { IceCardComponent } from '../atoms/ice-card/ice-card.component';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { SpinnerComponent } from '../atoms/spinner/spinner.component';
import { SimTypeSupport } from '@app/sales/modules/mobilephone/models/sim-type-support';
import { IceImgPillComponent } from '../atoms/ice-img-pill/ice-img-pill.component';
import { IceBadgeComponent } from '../atoms/ice-badge/ice-badge.component';

enum SortingTypes {
  LowestPrice = 'Stigende (pris)',
  HighestPrice = 'Synkende (pris)',
}

@Component({
  selector: 'app-hardware-search-result',
  templateUrl: './hardware-search-result.component.html',
  styleUrls: ['./hardware-search-result.component.scss'],
  imports: [
    SpinnerComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    IceCardComponent,
    NgClass,
    AllowSettingsDirective,
    PriceFormatPipe,
    IceImgPillComponent,
    IceBadgeComponent,
  ],
})
export class HardwareSearchResultComponent {
  PriceType = PriceType;
  SearchDisplayType = SearchDisplayType;
  DealerClaimType = DealerClaimType;
  SortingTypes = SortingTypes;
  IconNames = IconNames;
  SimTypeSupport = SimTypeSupport;

  @Input() searchResultPhoneGroups: HardwareVariantGroup[];
  @Input() accessories: AccessoryItem[];
  @Input() isLoadingSearchResults: boolean;
  @Input() loadingVariantsGroupSlug: string = null;
  @Input() displayType: string;
  @Input() defaultImage: string;
  @Input() isAccessories: boolean = false;
  @Input() showEquipmentUpgradePriceOnly: boolean;
  @Input() isManualRegistrationSupported: boolean;

  @Output() changeDisplayType = new EventEmitter();
  @Output() itemClicked = new EventEmitter();
  @Output() registerManually = new EventEmitter();

  sortBy: SortingTypes = SortingTypes.HighestPrice;

  canUseManualRegistration = select(DealerStateSelectors.canUseManualRegistration);

  get searchResultLength(): number {
    return (this.isAccessories ? this.accessories?.length : this.searchResultPhoneGroups?.length) ?? 0;
  }

  groupWaitingForVariants(groupSlug: string): HardwareVariantGroup {
    return this.searchResultPhoneGroups.find(g => g.groupSlug === groupSlug);
  }

  changeSorting(sortingOption: string) {
    this.sortBy = sortingOption as SortingTypes;
  }

  get sortedPhoneResults(): HardwareVariantGroup[] {
    return this.searchResultPhoneGroups
      .slice()
      .sort((a, b) =>
        this.sortBy === SortingTypes.LowestPrice
          ? a.hardwareOnlyPriceFrom - b.hardwareOnlyPriceFrom
          : b.hardwareOnlyPriceFrom - a.hardwareOnlyPriceFrom
      );
  }

  get sortedAccessoryResults(): AccessoryItem[] {
    return this.accessories
      .slice()
      .sort((a, b) =>
        this.sortBy === SortingTypes.LowestPrice
          ? a.priceInformation.price.includingVAT - b.priceInformation.price.includingVAT
          : b.priceInformation.price.includingVAT - a.priceInformation.price.includingVAT
      );
  }

  get sortingOptions(): SortingTypes[] {
    return [SortingTypes.LowestPrice, SortingTypes.HighestPrice];
  }

  getCampaignSavings(hardwareVariant: HardwareVariantGroup): number | null {
    return hardwareVariant.bestTotalCampaignDiscountAmount > 0 ? hardwareVariant.bestTotalCampaignDiscountAmount : null;
  }
}
